import React, { useState, useRef } from 'react';
import { Lock, Loader2, User, KeyRound, Smartphone } from 'lucide-react';
import { ErpRole, erpRoleFromSupplierRole, storeErpSession } from './erpAccess';
import { supplierLogin, requestSupplierSmsCode, resetSupplierPassword } from '../../api/supplier';
import { chineseErrorMessage } from '../../api/errorMessage';

interface ErpUnlockScreenProps {
    onUnlock: (role: ErpRole) => void;
}

export const ErpUnlockScreen: React.FC<ErpUnlockScreenProps> = ({ onUnlock }) => {
    const [mode, setMode] = useState<'login' | 'reset'>('login');
    const [account, setAccount] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [shakeKey, setShakeKey] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Password-reset (SMS placeholder) state.
    const [resetPhone, setResetPhone] = useState('');
    const [resetTicket, setResetTicket] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [resetNewPw, setResetNewPw] = useState('');

    const fail = (msg: string) => { setError(msg); setNotice(null); setShakeKey(k => k + 1); };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const acct = account.trim();
        if (!acct || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            // 供应商：手机号 + 密码；团队：主控码填在账号、密码留空。后端返回 role。
            const result = await supplierLogin(acct, password.trim() || undefined);
            if ((result.role as string) === 'employee') {
                // 录品员的作用域按当日任务动态切换，网页端不支持 —— 引导去 App。
                fail('录品员请使用 App 登录');
                setPassword('');
                return;
            }
            storeErpSession({ token: result.token, role: result.role, supplier: result.supplier });
            onUnlock(erpRoleFromSupplierRole(result.role));
        } catch (err) {
            fail(chineseErrorMessage(err, '账号或密码不正确'));
            setPassword('');
            setTimeout(() => inputRef.current?.focus(), 0);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestCode = async () => {
        const phone = resetPhone.trim();
        if (!phone || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const r = await requestSupplierSmsCode(phone);
            setResetTicket(r.ticket);
            setNotice(r.dev_code ? `验证码已发送（开发环境：${r.dev_code}）` : '验证码已发送，请查收短信');
        } catch (err) {
            fail(chineseErrorMessage(err, '发送验证码失败'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        if (!resetTicket) { fail('请先获取验证码'); return; }
        if (resetNewPw.trim().length < 6) { fail('新密码至少 6 位'); return; }
        setSubmitting(true);
        setError(null);
        try {
            await resetSupplierPassword({
                phone: resetPhone.trim(), ticket: resetTicket,
                code: resetCode.trim(), new_password: resetNewPw.trim(),
            });
            setMode('login');
            setAccount(resetPhone.trim());
            setPassword('');
            setNotice('密码已重置，请用新密码登录');
            setResetTicket(''); setResetCode(''); setResetNewPw('');
        } catch (err) {
            fail(chineseErrorMessage(err, '重置失败'));
        } finally {
            setSubmitting(false);
        }
    };

    const inputStyle = (isError: boolean): React.CSSProperties => ({
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${isError ? 'rgba(239,68,68,0.45)' : 'rgba(255,255,255,0.07)'}`,
        caretColor: 'white',
    });
    const inputCls = 'w-full rounded-xl py-3.5 pl-11 pr-4 text-sm text-white placeholder-slate-600 outline-none transition-all duration-200';

    return (
        <div
            className="fixed inset-0 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1a2744 50%, #0f172a 100%)' }}
        >
            {/* Subtle dot-grid background */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px)',
                    backgroundSize: '32px 32px',
                }}
            />

            <div
                key={shakeKey}
                className="relative z-10 w-full max-w-xs mx-6"
                style={shakeKey > 0 ? { animation: 'erp-shake 0.42s ease-in-out' } : undefined}
            >
                {/* Logo + subtitle */}
                <div className="text-center mb-12">
                    <img
                        src="/images/Logo_Color.svg"
                        alt="SSEEAA"
                        className="h-6 mx-auto mb-5"
                        style={{ filter: 'brightness(0) invert(1)', opacity: 0.8 }}
                    />
                    <p
                        className="text-xs tracking-[0.3em] uppercase"
                        style={{ color: 'rgba(148,163,184,0.6)' }}
                    >
                        Management System
                    </p>
                </div>

                {mode === 'login' ? (
                <form onSubmit={handleSubmit}>
                    <div className="relative mb-3">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'rgba(100,116,139,0.8)' }} />
                        <input
                            ref={inputRef}
                            type="text"
                            value={account}
                            onChange={(e) => { setAccount(e.target.value); setError(null); }}
                            placeholder="手机号 / 访问码"
                            autoFocus
                            autoComplete="username"
                            className={inputCls}
                            style={inputStyle(!!error)}
                        />
                    </div>
                    <div className="relative mb-3">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'rgba(100,116,139,0.8)' }} />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setError(null); }}
                            placeholder="密码（团队访问码免填）"
                            autoComplete="current-password"
                            className={inputCls}
                            style={inputStyle(!!error)}
                        />
                    </div>

                    {error && <p className="text-red-400 text-xs text-center mb-3">{error}</p>}
                    {notice && !error && <p className="text-emerald-300/90 text-xs text-center mb-3">{notice}</p>}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 mt-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: 'rgba(255,255,255,0.92)', color: '#0f172a' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,1)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.92)'; }}
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {submitting ? '验证中…' : '进入系统'}
                    </button>

                    <button
                        type="button"
                        onClick={() => { setMode('reset'); setError(null); setNotice(null); }}
                        className="w-full mt-3 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                        供应商首次登录 / 忘记密码？
                    </button>
                </form>
                ) : (
                <form onSubmit={handleReset}>
                    <div className="relative mb-3">
                        <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'rgba(100,116,139,0.8)' }} />
                        <input
                            type="tel"
                            value={resetPhone}
                            onChange={(e) => { setResetPhone(e.target.value); setError(null); }}
                            placeholder="手机号"
                            autoFocus
                            className={inputCls}
                            style={inputStyle(!!error)}
                        />
                    </div>
                    <div className="relative mb-3 flex gap-2">
                        <div className="relative flex-1">
                            <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'rgba(100,116,139,0.8)' }} />
                            <input
                                type="text"
                                inputMode="numeric"
                                value={resetCode}
                                onChange={(e) => { setResetCode(e.target.value); setError(null); }}
                                placeholder="验证码"
                                className={inputCls}
                                style={inputStyle(!!error)}
                            />
                        </div>
                        <button
                            type="button"
                            onClick={handleRequestCode}
                            disabled={submitting || !resetPhone.trim()}
                            className="px-3 rounded-xl text-xs font-medium whitespace-nowrap transition-colors disabled:opacity-50"
                            style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(226,232,240,0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
                        >
                            获取验证码
                        </button>
                    </div>
                    <div className="relative mb-3">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'rgba(100,116,139,0.8)' }} />
                        <input
                            type="password"
                            value={resetNewPw}
                            onChange={(e) => { setResetNewPw(e.target.value); setError(null); }}
                            placeholder="新密码（至少 6 位）"
                            autoComplete="new-password"
                            className={inputCls}
                            style={inputStyle(!!error)}
                        />
                    </div>

                    {error && <p className="text-red-400 text-xs text-center mb-3">{error}</p>}
                    {notice && !error && <p className="text-emerald-300/90 text-xs text-center mb-3">{notice}</p>}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3.5 rounded-xl text-sm font-medium transition-all duration-200 mt-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ background: 'rgba(255,255,255,0.92)', color: '#0f172a' }}
                    >
                        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {submitting ? '提交中…' : '重置密码'}
                    </button>

                    <button
                        type="button"
                        onClick={() => { setMode('login'); setError(null); setNotice(null); }}
                        className="w-full mt-3 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                    >
                        ← 返回登录
                    </button>
                </form>
                )}
            </div>

            <style>{`
                @keyframes erp-shake {
                    0%, 100% { transform: translateX(0); }
                    15%       { transform: translateX(-9px); }
                    35%       { transform: translateX(9px); }
                    55%       { transform: translateX(-5px); }
                    75%       { transform: translateX(5px); }
                }
            `}</style>
        </div>
    );
};
